import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';

export interface NotificationTemplate {
    id?: number;
    name: string;
    title: string;
    body: string;
    dataJson?: string;
    imageUrl?: string;
    priority: number;
}

export interface IntervalConfig {
    id?: number;
    name: string;
    minIntervalHours: number;
    maxIntervalHours: number;
    templateId?: number | null;
    templateName?: string;
    quietHoursEnabled: boolean;
    quietHoursStart?: string;
    quietHoursEnd?: string;
}

export interface DailySchedule {
    id?: number;
    name: string;
    hour: number;
    minute: number;
    templateId: number;
    templateName?: string;
    active?: boolean;
    lastFiredAt?: string;
}

export interface SendNotification {
    userId?: number | null;
    sendToAll: boolean;
    title: string;
    body: string;
    priority: number;
}

export interface SendResult {
    totalTargets: number;
    sentCount: number;
    failedCount: number;
}

export interface PushConfig {
    provider: number; // 1 = FCM, 2 = APNs
    credentialsJson: string;
    bundleId?: string;
    environment?: string;
}

@Injectable({
    providedIn: 'root'
})
export class NotificationAdminService {
    private base = `${environment.apiUrl}/admin/notifications`;

    constructor(private http: HttpClient) {}

    send(dto: SendNotification): Observable<SendResult> {
        return this.http.post<SendResult>(`${this.base}/send`, dto);
    }

    saveConfig(dto: PushConfig): Observable<void> {
        return this.http.post<void>(`${this.base}/config`, dto);
    }

    // Templates
    listTemplates(): Observable<NotificationTemplate[]> {
        return this.http.get<NotificationTemplate[]>(`${this.base}/templates?page=1&pageSize=100`);
    }
    createTemplate(t: NotificationTemplate): Observable<NotificationTemplate> {
        return this.http.post<NotificationTemplate>(`${this.base}/templates`, t);
    }
    updateTemplate(id: number, t: Partial<NotificationTemplate>): Observable<NotificationTemplate> {
        return this.http.put<NotificationTemplate>(`${this.base}/templates/${id}`, t);
    }
    deleteTemplate(id: number): Observable<void> {
        return this.http.delete<void>(`${this.base}/templates/${id}`);
    }

    // Interval configs
    listIntervals(): Observable<IntervalConfig[]> {
        return this.http.get<IntervalConfig[]>(`${this.base}/interval-configs?page=1&pageSize=100`);
    }
    createInterval(c: IntervalConfig): Observable<IntervalConfig> {
        return this.http.post<IntervalConfig>(`${this.base}/interval-configs`, c);
    }
    updateInterval(id: number, c: Partial<IntervalConfig>): Observable<IntervalConfig> {
        return this.http.put<IntervalConfig>(`${this.base}/interval-configs/${id}`, c);
    }
    deleteInterval(id: number): Observable<void> {
        return this.http.delete<void>(`${this.base}/interval-configs/${id}`);
    }

    // Daily schedules
    listSchedules(): Observable<DailySchedule[]> {
        return this.http.get<DailySchedule[]>(`${this.base}/daily-schedules?page=1&pageSize=100`);
    }
    createSchedule(s: DailySchedule): Observable<DailySchedule> {
        return this.http.post<DailySchedule>(`${this.base}/daily-schedules`, s);
    }
    updateSchedule(id: number, s: Partial<DailySchedule>): Observable<DailySchedule> {
        return this.http.put<DailySchedule>(`${this.base}/daily-schedules/${id}`, s);
    }
    deleteSchedule(id: number): Observable<void> {
        return this.http.delete<void>(`${this.base}/daily-schedules/${id}`);
    }
}
